/**
 * Decoupled cookie API. Wrapper around `js-cookie` that lets you get/set
 * fields on a JSON object in a cookie.
 *
 * @author    Will Van Rensselaer <wvanrensselaer@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */
/* eslint-disable consistent-return */
import Cookies from 'js-cookie';
import {getCookieDomain} from '../cookie_utils';

/**
 * Encodes an object of cookies into query string format.
 *
 * @param {object} cookies A key: value object of cookies
 * @returns {string}
 */
function encodeSubCookies(cookies) {
  return Object.entries(cookies)
    .map(([key, value]) => {
      return value ? `${key}=${value}` : key;
    })
    .join('&');
}

/**
 * Decodes a query string formatted sub-cookie string into a cookie object.
 *
 * @param {string} cookies The cookie string to decode
 * @returns {object}
 */
function decodeSubCookies(cookies) {
  if (cookies.trim().length === 0) {
    return {};
  }

  return cookies
    .split('&')
    .map((cookie) => {
      const index = cookie.indexOf('=');

      return index >= 0
        ? [cookie.slice(0, index), cookie.slice(index + 1)]
        : [cookie, ''];
    })
    .reduce((result, [key, value]) => {
      result[key] = value;

      return result;
    }, {});
}

/**
 * Sets a cookie.
 *
 * @param {string} key The cookie key
 * @param {any} value If this is an object or array, it will be serialized with
 *   `JSON.stringify`. Otherwise it will be cast to a string.
 * @param {object} options If a `field` option is provided, this will update
 *   that field on a JSON object stored in the cookie. All other options are
 *   passed as `js-cookie`'s `set` attributes.
 */
export function setCookie(
  key,
  value,
  {field, asJSON, domain = getCookieDomain(), ...attributes} = {}
) {
  if (field) {
    const root = asJSON
      ? Cookies.getJSON(key) || {}
      : decodeSubCookies(Cookies.get(key) || '');

    return Cookies.set(
      key,
      asJSON
        ? {...root, [field]: value}
        : encodeSubCookies({...root, [field]: value}),
      {domain, ...attributes}
    );
  }
  return Cookies.set(key, value, {domain, ...attributes});
}

/**
 * Gets a cookie.
 *
 * @param {string} key The cookie key
 * @param {object} options If the `field` option is provided, it is assumed the
 *   cookie value is a JSON object and will return the value of that field on
 *   the object. If the `asJSON` option is provided, the value will be parsed
 *   with `JSON.parse` or the unparsed value will be returned if it's not valid
 *   JSON.
 */
export function getCookie(key, {field, asJSON} = {}) {
  if (field) {
    const root = asJSON
      ? Cookies.getJSON(key) || {}
      : decodeSubCookies(Cookies.get(key) || '');

    if (typeof root !== 'object' || Array.isArray(root)) {
      return;
    }

    return root[field];
  }

  return asJSON ? Cookies.getJSON(key) : Cookies.get(key);
}

/**
 * Removes a cookie.
 *
 * @param {string} key The cookie key
 * @param {object} options If the `field` option is provided, this will remove
 *   that field on the JSON object stored in the cookie. If the resulting JSON
 *   object has no keys, the cookie is deleted. Other options are passed as
 *   `js-cookie`'s `set` attributes.
 */
export function removeCookie(key, {field, asJSON, ...attributes} = {}) {
  if (field) {
    const root = asJSON
      ? Cookies.getJSON(key) || {}
      : decodeSubCookies(Cookies.get(key) || '');

    if (typeof root !== 'object' || Array.isArray(root)) {
      return;
    }

    const {[field]: value, ...newRoot} = root;
    return Object.keys(newRoot).length === 0
      ? Cookies.remove(key, attributes)
      : setCookie(
          key,
          asJSON ? newRoot : encodeSubCookies(newRoot),
          attributes
        );
  }
  return Cookies.remove(key, attributes);
}
